import './App.css';
import { useEffect, useState, useCallback } from 'react';
import Block from './components/Block/Block';
import { validateWord } from './services/apiService';

function App() {

  const COLUMNS = 5;
  const ROWS = 6;
  const NUMBER_OF_WORDS = 5;
  const LENGTH_OF_WORD = 5;
  const MAX_RETRY = 5;

  const [loading, setLoading] = useState(true);
  const [playerWon, setPlayerWon] = useState(false);
  const [playerLose, setPlayerLose] = useState(false);
  const [validatingWord, setIsValidatingWord] = useState(false);
  const [wordOfTheDay, setWordOfTheDay] = useState();
  const [guesses, setGuesses] = useState([]);
  const [error, setError] = useState(null);
  const [board, setBoard] = useState(
    Array.from({ length: ROWS }, () =>
      Array.from({ length: COLUMNS }, () => {
        return {
          value: "",
          isInCorrectPlace: false,
          charExistsInWord: false
        }
      })
    )
  );

  const [currentRow, setCurrentRow] = useState(0);
  const [currentCol, setCurrentCol] = useState(0);

  const fetchWordle = useCallback((attempt = 1) => {
    setLoading(true);
    fetch(`https://random-words-api.kushcreates.com/api?language=en&category=wordle&length=${LENGTH_OF_WORD}&words=${NUMBER_OF_WORDS}`)
      .then(response => {
        if (!response.ok) {
          throw new Error("Error Calling API");
        }
        return response.json();
      })
      .then(resp => {
        const words = resp.map(wordle => wordle["word"]);

        // Pick a random word
        const randomNum = Math.floor(Math.random() * words.length);
        const selectedWord = words[randomNum];

        return { selectedWord, words };
      })
      .then(async ({ selectedWord, words }) => {
        // Validate only the selected word
        const isValid = await validateWord(selectedWord);
        return ({ selectedWord, words, isValid });
      })
      .then(({ selectedWord, words, isValid }) => {
        if (!isValid) {
          console.log(`Selected word "${selectedWord}" is invalid on attempt ${attempt}`);

          // Retry if we haven't exceeded max attempts
          if (attempt < MAX_RETRY) {
            console.log(`Retrying... (attempt ${attempt + 1} / ${MAX_RETRY})`);
            return fetchWordle(attempt + 1);
          } else {
            throw new Error(`Failed to find valid word after ${MAX_RETRY} attempts`);
          }
        }

        setWordOfTheDay(selectedWord);
        setLoading(false);
      })
      .catch(err => {
        console.error("Error fetching wordle:", err);
        setLoading(false);
      });
  }, [LENGTH_OF_WORD, NUMBER_OF_WORDS]);

  const resetGame = () => {
    setPlayerWon(false);
    setPlayerLose(false);
    setGuesses([]);
    setCurrentRow(0);
    setCurrentCol(0);
    setBoard(
      Array.from({ length: ROWS }, () =>
        Array.from({ length: COLUMNS }, () => {
          return {
            value: "",
            isInCorrectPlace: false,
            charExistsInWord: false
          }
        })
      )
    );
    fetchWordle();
  };

  useEffect(() => {
    fetchWordle();
  }, [fetchWordle]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    const handleKeyDown = async (event) => {
      const key = event.key;

      // Stop Tracking if player already won or lose
      if (playerWon) return;
      if (playerLose) return;

      if (validatingWord) return;
      if (loading) return;

      console.log(wordOfTheDay);

      // Only allow letters
      if (/^[a-zA-Z]$/.test(key)) {
        if (currentCol >= COLUMNS) return;
        setBoard(prev =>
          prev.map((row, rIdx) =>
            row.map((cell, cIdx) => {
              if (rIdx === currentRow && cIdx === currentCol) {
                cell.value = key.toUpperCase()
                return cell
              } else {
                return cell;
              }
            })
          )
        );

        // Move right, but stop at last column
        setCurrentCol(col => Math.min(col + 1, COLUMNS));
      }

      // ENTER → move to next row
      if (key === "Enter" && !validatingWord) {
        if (currentRow >= ROWS) return;

        const word = board[currentRow].map(cell => cell.value).join("");
        if (word.length !== 5) return;

        const exists = guesses.some(
          item => item.toLowerCase() === word.toLowerCase()
        );

        if (exists) {
          setError(`You already enter the word ${word}`)
          setIsValidatingWord(false);
          return;
        }

        validateWord(word).then(isValid => {
          setIsValidatingWord(true);
          if (!isValid) {
            console.log("Invalid word!");
            setError(`${word} is an invalid word`)
            setIsValidatingWord(false);
            return;
          }

          // Add the guess
          setGuesses(prev => {
            const newGuesses = [...prev, word];

            // Check for win
            if (word.toLowerCase() === wordOfTheDay.toLowerCase()) {
              setPlayerWon(true);
              setIsValidatingWord(false);
            }

            // Check for lose
            if (newGuesses.length === ROWS && !playerWon) {
              setPlayerLose(true);
              setIsValidatingWord(false);
            }

            return newGuesses;
          });

          // Update board with proper letter frequency handling
          setBoard(prev =>
            prev.map((row, rIdx) => {
              if (rIdx !== currentRow) return row;

              const lowerWord = word.toLowerCase();
              const lowerTarget = wordOfTheDay.toLowerCase();

              // Track which letters in target have been matched
              const targetLetterCount = {};
              for (let char of lowerTarget) {
                targetLetterCount[char] = (targetLetterCount[char] || 0) + 1;
              }

              // First pass: mark correct positions and decrease count
              const results = Array(COLUMNS).fill(null).map(() => ({
                isInCorrectPlace: false,
                charExistsInWord: false
              }));

              for (let i = 0; i < COLUMNS; i++) {
                if (lowerWord[i] === lowerTarget[i]) {
                  results[i].isInCorrectPlace = true;
                  targetLetterCount[lowerWord[i]]--;
                }
              }

              // Second pass: mark wrong positions only if letters are still available
              for (let i = 0; i < COLUMNS; i++) {
                if (!results[i].isInCorrectPlace) {
                  const letter = lowerWord[i];
                  if (targetLetterCount[letter] > 0) {
                    results[i].charExistsInWord = true;
                    targetLetterCount[letter]--;
                  }
                }
              }

              // Apply results to row
              return row.map((cell, cIdx) => ({
                ...cell,
                value: word[cIdx],
                isInCorrectPlace: results[cIdx].isInCorrectPlace,
                charExistsInWord: results[cIdx].charExistsInWord
              }));
            })
          );

          // Move cursor
          if (currentRow < ROWS - 1) {
            setCurrentRow(row => row + 1);
            setCurrentCol(0);
          }

          setIsValidatingWord(false)
        });
      }

      if (key === "Backspace") {
        if (currentCol === 0) return;

        setCurrentCol(col => col - 1);
        setBoard(prev =>
          prev.map((row, rIdx) =>
            row.map((cell, cIdx) => {
              if (rIdx === currentRow && cIdx === currentCol - 1) {
                cell.value = ""
                return cell
              } else {
                return cell;
              }
            })
          )
        );
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentRow, currentCol, playerWon, playerLose, board, wordOfTheDay, validatingWord, loading, ROWS, COLUMNS, guesses]);

  if (loading) {
    return (
      <div className="App">
        <div className="game-header">
          <h1 className="game-title">Wordle</h1>
          <p className="game-subtitle">Guess the 5-letter word</p>
        </div>
        <div className="game-container">
          <div className="loading-container">
            <div className="spinner"></div>
            <p className="loading-text">Loading word...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <div className="game-header">
        <h1 className="game-title">Wordle</h1>
        <p className="game-subtitle">Guess the 5-letter word</p>
      </div>

      <div className="game-container">
        {error && (
          <div className="error-popup">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        <div className="board">
          {board.map((row, rIdx) => (
            <div key={rIdx} className="board-row">
              {row.map((cell, cIdx) => (
                <Block
                  key={cIdx}
                  block={cell}
                  isActive={rIdx === currentRow && cIdx === currentCol && !playerWon && !playerLose}
                />
              ))}
            </div>
          ))}
        </div>

        {playerWon && (
          <div className="status-message status-win">
            🎉 You Won! 🎉
          </div>
        )}

        {playerLose && !playerWon && (
          <div className="status-message status-lose">
            😢 Game Over
            <div style={{ fontSize: '1.2rem', marginTop: '0.5rem' }}>
              The word was: <strong>{wordOfTheDay.toUpperCase()}</strong>
            </div>
          </div>
        )}

        {(playerWon || playerLose) && (
          <button className="restart-button" onClick={resetGame}>
            🔄 Play Again
          </button>
        )}

        <div className="game-info">
          <p>💡 Type letters and press ENTER to submit</p>
          <p>🎯 Guesses: {guesses.length}/{ROWS}</p>
        </div>
      </div>
    </div>
  );
}

export default App;